import {
  Box,
  Button,
  CircularProgress,
  Grid2,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import axios from "axios";
import { viewPDF } from "../../../utils/viewPDF";
import { uploadFile } from "../../../utils/uploadToAzure";

const Step1 = ({
  tender,
  setStep,
  bid,
  setBid,
  storedUser,
  fileNames,
  setFileNames,
}) => {
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const vendorId = JSON.parse(localStorage.getItem("vendor"));
  const [btnLoading, setBtnLoading] = useState(false);

  const [uploading, setUploading] = useState({
    ts1: false,
    ts2: false,
    ts3: false,
    ts4: false,
    ts5: false,
    ts6: false,
    ts7: false,
    ts8: false,
  });

  const setFieldLoading = (field, isLoading) => {
    setUploading((prev) => ({
      ...prev,
      [field]: isLoading,
    }));
  };

  const handleFileUpload = async (e) => {
    const { name, files } = e.target;
    setFieldLoading(name, true);
    if (files && files.length > 0) {
      const file = files[0]; // Get the first file

      // Update the file names state
      setFileNames((prev) => ({
        ...prev,
        [name]: file.name, // Set the filename in state
      }));

      const formData = new FormData();
      formData.append("file", file);

      try {
        const res = await uploadFile(file, tender.orgId, tender.tenderId, storedUser)
        // Set the received file path in tender state
        setBid({ ...bid, [e.target.name]: res });
        setErrors({ ...errors, [e.target.name]: "" });
      } catch (error) {
        setErrors({
          ...errors,
          [e.target.name]:
            "Cannot upload file. Make sure file size is less than 1MB",
        });
      } finally {
        setFieldLoading(name, false);
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    for (let i = 1; i <= 8; i++) {
      const fieldKey = `ts${i}`;
      const bidKey = `ts${i}`;

      // If the tender specifies that this document is required
      if (tender?.[fieldKey]) {
        // And the bid does not have the required document
        if (!bid?.[bidKey]) {
          newErrors[bidKey] = "This document is required";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = async () => {
    if (Object.values(errors).some((err) => err)) {
      return;
    }
    if (!validateForm()) return;
    setBtnLoading(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/draft-bid`,
        {
          vendorId: vendorId,
          tenderId: tender.tenderId,
          ts1: bid.ts1,
          ts2: bid.ts2,
          ts3: bid.ts3,
          ts4: bid.ts4,
          ts5: bid.ts5,
          ts6: bid.ts6,
          ts7: bid.ts7,
          ts8: bid.ts8,
          ts1Filename: fileNames.ts1,
          ts2Filename: fileNames.ts2,
          ts3Filename: fileNames.ts3,
          ts4Filename: fileNames.ts4,
          ts5Filename: fileNames.ts5,
          ts6Filename: fileNames.ts6,
          ts7Filename: fileNames.ts7,
          ts8Filename: fileNames.ts8,
        },
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } }
      );
      setStep((prev) => prev + 1);
    } catch (error) {
      console.log(error);
    } finally {
      setBtnLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!validateForm()) return;
    setBtnLoading(true);
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/draft-bid`,
        {
          vendorId: vendorId,
          tenderId: tender.tenderId,
          ts1: bid.ts1,
          ts2: bid.ts2,
          ts3: bid.ts3,
          ts4: bid.ts4,
          ts5: bid.ts5,
          ts6: bid.ts6,
          ts7: bid.ts7,
          ts8: bid.ts8,
          ts1Filename: fileNames.ts1,
          ts2Filename: fileNames.ts2,
          ts3Filename: fileNames.ts3,
          ts4Filename: fileNames.ts4,
          ts5Filename: fileNames.ts5,
          ts6Filename: fileNames.ts6,
          ts7Filename: fileNames.ts7,
          ts8Filename: fileNames.ts8,
        },
        { headers: { Authorization: `Bearer ${storedUser.accessToken}` } }
      );
      setStep((prev) => prev + 1);
    } catch (error) {
      console.log(error);
    } finally {
      setBtnLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/vendor/dashboard");
  };
  return (
    <div style={{ position: "relative" }}>
      {btnLoading && <div className="loading-div"></div>}
      <div className="dashboard-container">
        <Typography className="form-title">Technical Specifications</Typography>
        <Grid2 container columnSpacing={2}>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            <label htmlFor="ts1">
              TS-1 ({tender?.ts1Title || ""})
              <div className={`input-file ${errors.ts1 && "input-error"}`}>
                <input
                  type="file"
                  id="ts1"
                  name="ts1"
                  onChange={handleFileUpload}
                />
                <span
                  style={{
                    width: "80%",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                >
                  {fileNames.ts1 || "No file chosen"}
                </span>
                <AttachFileIcon
                  fontSize="small"
                  sx={{
                    color: "#555",
                    transform: "rotate(90deg)",
                    "&:hover": { color: "#1A71F6BF" },
                  }}
                />
              </div>
            </label>
            {errors.ts1 && <p className="error">{errors.ts1}</p>}
            {uploading.ts1 && <span style={{fontSize: '12px'}}>Uploading File... <CircularProgress size="30px" /></span>}
            {bid && bid.ts1 && (
              <Button
                sx={{
                  margin: "-6% 0 0 0 !important",
                  width: "max-content !important",
                  fontSize: "12px !important",
                  textDecoration: "underline",
                  textAlign: "start",
                }}
                onClick={() => viewPDF(bid.ts1, storedUser)}
              >
                {fileNames.ts1}
              </Button>
            )}
            {tender?.ts1 && (
              <Box>
                <Button
                  className="btn-outlined btn-view-doc"
                  onClick={() => viewPDF(tender.ts1, storedUser)}
                >
                  View TS-1 Document
                </Button>
              </Box>
            )}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            {tender?.ts2 && (
              <>
                <label htmlFor="ts2">
                  TS-2 ({tender?.ts2Title || ""})
                  <div className="input-file">
                    <input
                      type="file"
                      id="ts2"
                      name="ts2"
                      onChange={handleFileUpload}
                    />
                    <span
                      style={{
                        width: "80%",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                      }}
                    >
                      {fileNames.ts2 || "No file chosen"}
                    </span>
                    <AttachFileIcon
                      fontSize="small"
                      sx={{
                        color: "#555",
                        transform: "rotate(90deg)",
                        "&:hover": { color: "#1A71F6BF" },
                      }}
                    />
                  </div>
                </label>
                {errors.ts2 && <p className="error">{errors.ts2}</p>}
                {uploading.ts2 && <span style={{fontSize: '12px'}}>Uploading File... <CircularProgress size="30px" /></span>}
                {bid && bid.ts2 && (
                  <div>
                    <Button
                      sx={{
                        margin: "-6% 0 0 0 !important",
                        width: "max-content !important",
                        fontSize: "12px !important",
                        textDecoration: "underline",
                        textAlign: "start",
                      }}
                      onClick={() => viewPDF(bid.ts2, storedUser)}
                    >
                      {fileNames.ts2}
                    </Button>
                  </div>
                )}
                {tender?.ts2 && (
                  <Button
                    className="btn-outlined btn-view-doc"
                    onClick={() => viewPDF(tender.ts2, storedUser)}
                  >
                    View TS-2 Document
                  </Button>
                )}
              </>
            )}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            {tender?.ts3 && (
              <>
                <label htmlFor="ts3">
                  TS-3 ({tender?.ts3Title || ""})
                  <div className="input-file">
                    <input
                      type="file"
                      id="ts3"
                      name="ts3"
                      onChange={handleFileUpload}
                    />
                    <span
                      style={{
                        width: "80%",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                      }}
                    >
                      {fileNames.ts3 || "No file chosen"}
                    </span>
                    <AttachFileIcon
                      fontSize="small"
                      sx={{
                        color: "#555",
                        transform: "rotate(90deg)",
                        "&:hover": { color: "#1A71F6BF" },
                      }}
                    />
                  </div>
                </label>
                {errors.ts3 && <p className="error">{errors.ts3}</p>}
                {uploading.ts3 && <span style={{fontSize: '12px'}}>Uploading File... <CircularProgress size="30px" /></span>}
                {bid && bid.ts3 && (
                  <div>
                    <Button
                      sx={{
                        margin: "-6% 0 0 0 !important",
                        width: "max-content !important",
                        fontSize: "12px !important",
                        textDecoration: "underline",
                        textAlign: "start",
                      }}
                      onClick={() => viewPDF(bid.ts3, storedUser)}
                    >
                      {fileNames.ts3}
                    </Button>
                  </div>
                )}
                {tender?.ts3 && (
                  <Button
                    className="btn-outlined btn-view-doc"
                    onClick={() => viewPDF(tender.ts3, storedUser)}
                  >
                    View TS-3 Document
                  </Button>
                )}
              </>
            )}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            {tender?.ts4 && (
              <>
                <label htmlFor="ts4">
                  TS-4 ({tender?.ts4Title || ""})
                  <div className="input-file">
                    <input
                      type="file"
                      id="ts4"
                      name="ts4"
                      onChange={handleFileUpload}
                    />
                    <span
                      style={{
                        width: "80%",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                      }}
                    >
                      {fileNames.ts4 || "No file chosen"}
                    </span>
                    <AttachFileIcon
                      fontSize="small"
                      sx={{
                        color: "#555",
                        transform: "rotate(90deg)",
                        "&:hover": { color: "#1A71F6BF" },
                      }}
                    />
                  </div>
                </label>
                {errors.ts4 && <p className="error">{errors.ts4}</p>}
                {uploading.ts4 && <span style={{fontSize: '12px'}}>Uploading File... <CircularProgress size="30px" /></span>}
                {bid && bid.ts4 && (
                  <div>
                    <Button
                      sx={{
                        margin: "-6% 0 0 0 !important",
                        width: "max-content !important",
                        fontSize: "12px !important",
                        textDecoration: "underline",
                        textAlign: "start",
                      }}
                      onClick={() => viewPDF(bid.ts4, storedUser)}
                    >
                      {fileNames.ts4}
                    </Button>
                  </div>
                )}
                {tender?.ts4 && (
                  <Button
                    className="btn-outlined btn-view-doc"
                    onClick={() => viewPDF(tender.ts4, storedUser)}
                  >
                    View TS-4 Document
                  </Button>
                )}
              </>
            )}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            {tender?.ts5 && (
              <>
                <label htmlFor="ts5">
                  TS-5 ({tender?.ts5Title || ""})
                  <div className="input-file">
                    <input
                      type="file"
                      id="ts5"
                      name="ts5"
                      onChange={handleFileUpload}
                    />
                    <span
                      style={{
                        width: "80%",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                      }}
                    >
                      {fileNames.ts5 || "No file chosen"}
                    </span>
                    <AttachFileIcon
                      fontSize="small"
                      sx={{
                        color: "#555",
                        transform: "rotate(90deg)",
                        "&:hover": { color: "#1A71F6BF" },
                      }}
                    />
                  </div>
                </label>
                {errors.ts5 && <p className="error">{errors.ts5}</p>}
                {uploading.ts5 && <span style={{fontSize: '12px'}}>Uploading File... <CircularProgress size="30px" /></span>}
                {bid && bid.ts5 && (
                  <div>
                    <Button
                      sx={{
                        margin: "-6% 0 0 0 !important",
                        width: "max-content !important",
                        fontSize: "12px !important",
                        textDecoration: "underline",
                        textAlign: "start",
                      }}
                      onClick={() => viewPDF(bid.ts5, storedUser)}
                    >
                      {fileNames.ts5}
                    </Button>
                  </div>
                )}
                {tender?.ts5 && (
                  <Button
                    className="btn-outlined btn-view-doc"
                    onClick={() => viewPDF(tender.ts5, storedUser)}
                  >
                    View TS-5 Document
                  </Button>
                )}
              </>
            )}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            {tender?.ts6 && (
              <>
                <label htmlFor="ts6">
                  TS-6 ({tender?.ts6Title || ""})
                  <div className="input-file">
                    <input
                      type="file"
                      id="ts6"
                      name="ts6"
                      onChange={handleFileUpload}
                    />
                    <span
                      style={{
                        width: "80%",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                      }}
                    >
                      {fileNames.ts6 || "No file chosen"}
                    </span>
                    <AttachFileIcon
                      fontSize="small"
                      sx={{
                        color: "#555",
                        transform: "rotate(90deg)",
                        "&:hover": { color: "#1A71F6BF" },
                      }}
                    />
                  </div>
                </label>
                {errors.ts6 && <p className="error">{errors.ts6}</p>}
                {uploading.ts6 && <span style={{fontSize: '12px'}}>Uploading File... <CircularProgress size="30px" /></span>}
                {bid && bid.ts6 && (
                  <div>
                    <Button
                      sx={{
                        margin: "-6% 0 0 0 !important",
                        width: "max-content !important",
                        fontSize: "12px !important",
                        textDecoration: "underline",
                        textAlign: "start",
                      }}
                      onClick={() => viewPDF(bid.ts6, storedUser)}
                    >
                      {fileNames.ts6}
                    </Button>
                  </div>
                )}
                {tender?.ts6 && (
                  <Button
                    className="btn-outlined btn-view-doc"
                    onClick={() => viewPDF(tender.ts6, storedUser)}
                  >
                    View TS-6 Document
                  </Button>
                )}
              </>
            )}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            {tender?.ts7 && (
              <>
                <label htmlFor="ts7">
                  TS-7 ({tender?.ts7Title || ""})
                  <div className="input-file">
                    <input
                      type="file"
                      id="ts7"
                      name="ts7"
                      onChange={handleFileUpload}
                    />
                    <span
                      style={{
                        width: "80%",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                      }}
                    >
                      {fileNames.ts7 || "No file chosen"}
                    </span>
                    <AttachFileIcon
                      fontSize="small"
                      sx={{
                        color: "#555",
                        transform: "rotate(90deg)",
                        "&:hover": { color: "#1A71F6BF" },
                      }}
                    />
                  </div>
                </label>
                {errors.ts7 && <p className="error">{errors.ts7}</p>}
                {uploading.ts7 && <span style={{fontSize: '12px'}}>Uploading File... <CircularProgress size="30px" /></span>}
                {bid && bid.ts7 && (
                  <div>
                    <Button
                      sx={{
                        margin: "-6% 0 0 0 !important",
                        width: "max-content !important",
                        fontSize: "12px !important",
                        textDecoration: "underline",
                        textAlign: "start",
                      }}
                      onClick={() => viewPDF(bid.ts7, storedUser)}
                    >
                      {fileNames.ts7}
                    </Button>
                  </div>
                )}
                {tender?.ts7 && (
                  <Button
                    className="btn-outlined btn-view-doc"
                    onClick={() => viewPDF(tender.ts7, storedUser)}
                  >
                    View TS-7 Document
                  </Button>
                )}
              </>
            )}
          </Grid2>
          <Grid2 size={{ xs: 12, sm: 4 }}>
            {tender?.ts8 && (
              <>
                <label htmlFor="ts8">
                  TS-8 ({tender?.ts8Title || ""})
                  <div className="input-file">
                    <input
                      type="file"
                      id="ts8"
                      name="ts8"
                      onChange={handleFileUpload}
                    />
                    <span
                      style={{
                        width: "80%",
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                      }}
                    >
                      {fileNames.ts8 || "No file chosen"}
                    </span>
                    <AttachFileIcon
                      fontSize="small"
                      sx={{
                        color: "#555",
                        transform: "rotate(90deg)",
                        "&:hover": { color: "#1A71F6BF" },
                      }}
                    />
                  </div>
                </label>
                {errors.ts8 && <p className="error">{errors.ts8}</p>}
                {uploading.ts8 && <span style={{fontSize: '12px'}}>Uploading File... <CircularProgress size="30px" /></span>}
                {bid && bid.ts8 && (
                  <div>
                    <Button
                      sx={{
                        margin: "-6% 0 0 0 !important",
                        width: "max-content !important",
                        fontSize: "12px !important",
                        textDecoration: "underline",
                        textAlign: "start",
                      }}
                      onClick={() => viewPDF(bid.ts8, storedUser)}
                    >
                      {fileNames.ts8}
                    </Button>
                  </div>
                )}
                {tender?.ts8 && (
                  <Button
                    className="btn-outlined btn-view-doc"
                    onClick={() => viewPDF(tender.ts8, storedUser)}
                  >
                    View TS-8 Document
                  </Button>
                )}
              </>
            )}
          </Grid2>
        </Grid2>
      </div>
      <Box className="btn-container" sx={{ gap: 3 }}>
        <Button className="btn-outlined" onClick={handleBack}>
          Back
        </Button>
        <Button
          className="btn-contained"
          onClick={bid && bid.draftBidId ? handleUpdate : handleNext}
        >
          {bid && bid.draftBidId ? "Update" : "Next"}
        </Button>
      </Box>
    </div>
  );
};

export default Step1;
